/**
 * Helpers de fuso do tenant — agendamento e exibição (DOC 12 · LAC-033).
 * Por quê: datas da API vêm em UTC; a UI deve falar o relógio da conta, não o do notebook.
 */

export const DEFAULT_TENANT_TIMEZONE = "America/Sao_Paulo";

/** Lê `tenant.timezone` do sessionStorage (login JWT) — relógio da conta / agendamento. */
export function readTenantTimezone(): string {
  try {
    const raw = sessionStorage.getItem("authUser");
    if (!raw) {
      return DEFAULT_TENANT_TIMEZONE;
    }
    const parsed = JSON.parse(raw);
    const tz = parsed?.tenant?.timezone;
    return typeof tz === "string" && tz.trim() !== ""
      ? tz.trim()
      : DEFAULT_TENANT_TIMEZONE;
  } catch {
    return DEFAULT_TENANT_TIMEZONE;
  }
}

/**
 * Fuso para exibição no painel: preferência do usuário, senão da conta (LAC-078).
 * Agendamento continua com readTenantTimezone().
 */
export function readDisplayTimezone(): string {
  try {
    const raw = sessionStorage.getItem("authUser");
    if (!raw) {
      return readTenantTimezone();
    }
    const parsed = JSON.parse(raw);
    const userTz = parsed?.user?.timezone;
    if (typeof userTz === "string" && userTz.trim() !== "") {
      return userTz.trim();
    }
    return readTenantTimezone();
  } catch {
    return readTenantTimezone();
  }
}

/**
 * datetime-local → parede `Y-m-d H:i:s` (sem toISOString do browser).
 */
export function wallClockFromDatetimeLocal(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const match = trimmed.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2})?$/
  );
  if (!match) {
    return null;
  }

  return `${match[1]} ${match[2]}${match[3] ?? ":00"}`;
}

/**
 * Converte parede do tenant em instante UTC (Date), via Intl (sem moment-timezone).
 */
export function wallClockInTimeZoneToDate(
  wall: string,
  timeZone: string
): Date | null {
  const match = wall
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const h = Number(match[4]);
  const mi = Number(match[5]);
  const s = Number(match[6] ?? 0);
  const wantedAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);

  let utcMs = wantedAsUtc;
  for (let i = 0; i < 3; i++) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(utcMs));

    const get = (type: string) =>
      Number(parts.find((p) => p.type === type)?.value ?? "0");

    const asWall = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour"),
      get("minute"),
      get("second")
    );
    utcMs += wantedAsUtc - asWall;
  }

  return new Date(utcMs);
}

/** Formata ISO UTC no fuso da conta (Carla vê o horário que ela agendou). */
export function formatInTenantTimezone(
  isoUtc: string | null | undefined,
  timeZone: string,
  locale: string
): string {
  if (!isoUtc) {
    return "—";
  }

  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(locale || "pt-BR", {
      timeZone,
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  } catch {
    return date.toLocaleString(locale || "pt-BR");
  }
}

/**
 * Rótulo amigável do fuso (i18n) com fallback ao IANA.
 * Ex.: t('timezone.label.America/Sao_Paulo') → "Brasília (UTC−3)".
 */
export function tenantTimezoneDisplayLabel(
  timeZone: string,
  t: (key: string) => string
): string {
  const key = `timezone.label.${timeZone}`;
  const labeled = t(key);
  if (labeled && labeled !== key) {
    return labeled;
  }
  return timeZone;
}

/** Texto relativo “em X minutos / horas / dias” a partir de um instante UTC. */
export function relativeFromNow(
  when: Date,
  t: (key: string, opts?: Record<string, string | number>) => string
): string {
  const diffMs = when.getTime() - Date.now();
  if (diffMs < -60_000) {
    return t("sending.form.scheduleInPastHint");
  }
  if (diffMs < 60_000) {
    return t("sending.form.scheduleSoon");
  }

  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) {
    return t("sending.form.scheduleInMinutes", { count: minutes });
  }

  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return t("sending.form.scheduleInHours", { count: hours });
  }

  const days = Math.round(hours / 24);
  return t("sending.form.scheduleInDays", { count: days });
}

/**
 * datetime-local “amanhã no mesmo horário da parede” no fuso da conta.
 * Por quê: CTA quando o cap estoura — Carla agenda no horário em que já está pensando enviar.
 */
export function tomorrowSameWallClockDatetimeLocal(timeZone: string): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");

  const y = get("year");
  const m = get("month");
  const d = get("day");
  const h = get("hour");
  const mi = get("minute");
  // +1 dia nos componentes da parede (não no instante do notebook).
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(
    next.getUTCDate()
  )}T${pad(h)}:${pad(mi)}`;
}

/**
 * @deprecated Preferir tomorrowSameWallClockDatetimeLocal — mantido para callers 9h fixos.
 */
export function tomorrowMorningDatetimeLocal(
  timeZone: string,
  hour = 9
): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = today.split("-").map((part) => Number(part));
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(
    next.getUTCDate()
  )}T${pad(hour)}:00`;
}

/** True se o instante cai depois do fim do dia UTC atual (cap diário já não é o de hoje). */
export function isAfterUtcDayEnd(when: Date): boolean {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);
  return when.getTime() > end.getTime();
}

/**
 * Próximo reset do cap (meia-noite UTC) formatado no fuso da conta.
 * Por quê: Carla não pensa em UTC — “zera às 21:00” (Brasília) é acionável.
 */
export function formatNextUtcDayResetLocal(
  timeZone: string,
  locale: string
): string {
  const now = new Date();
  const nextUtcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0
  );
  try {
    return new Intl.DateTimeFormat(locale || "pt-BR", {
      timeZone,
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(new Date(nextUtcMidnight));
  } catch {
    return new Date(nextUtcMidnight).toISOString();
  }
}
